import { createSignal } from 'solid-js';
import type { Session } from '@supabase/supabase-js';
import { db } from '~/lib/db';

// Whether there is anyone to show a book to.
//
// Row-level security already stops a signed-out visitor reading a single row, so
// this is not the security boundary and must never be mistaken for one — the
// database is. What it stops is the failure that boundary produces on its own: a
// signed-out app renders a complete, confident, entirely zero portfolio, because
// "you have no rows" and "you have nothing" are the same empty result set. The
// gate is there so the app says which one it is.

const [session, setSession] = createSignal<Session | null>(null);

// Distinct from `session() == null`. Resolving the stored session is async, and
// without this the app would paint the signed-out gate for a frame on every load
// before deciding the user was signed in after all.
const [checked, setChecked] = createSignal(false);

export { session, checked };

export const signedIn = (): boolean => session() != null;

export const initializeAuth = (): void => {
    void db.auth.getSession().then(({ data }) => {
        setSession(data.session);
        setChecked(true);
    });

    // Covers signing in or out in another tab, and the token refresh that
    // otherwise expires a session the app still believes in.
    db.auth.onAuthStateChange((_event, next) => {
        setSession(next);
        setChecked(true);
    });
};

export const signOut = async (): Promise<void> => {
    await db.auth.signOut();
};
